import { vec2 } from "gl-matrix";
import { getSelectedElementFullMatrix } from "@/utils/StageUtils";
import { objectToZoomedSpace } from "@/utils/ZoomUtils";
import {
  applyMatrixToObject,
  BOTTOM,
  CENTER,
  collectEdgeIntersections,
  collectEdgesFromCorners,
  getElementBoundingPoints,
  getFirstEdgeIntersection,
  getVerticalRayFromCenter,
  TOP,
} from "./BoundsHelpers.mjs";

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 * @typedef {import("@/types/MathTypes.mjs").Coordinates} Coordinates
 * @typedef {import("@/types/MathTypes.mjs").Ray} Ray
 * @typedef {import("./BoundsHelpers.mjs").Bounds} Bounds
 * @typedef {import("./BoundsHelpers.mjs").BoundingEdges} BoundingEdges
 * @typedef {import("./BoundsHelpers.mjs").MatrixBoundingCorners} MatrixBoundingCorners
 * @typedef {import("./BoundsHelpers.mjs").SingleEdgeIntersectionResult} SingleEdgeIntersectionResult
 */

export const ELEMENT_TOOLBAR_BUFFER = 10;
export const RAY_OFFSET_VALUE = 10000;

const HALF_PI = Math.PI / 2;

// Rotation matrices at 90°/180°/270° carry ~1e-16 sin/cos noise, so an edge
// that should be exactly vertical yields an intersection angle of
// HALF_PI ± ~1e-16 instead of exactly HALF_PI. Treat near-vertical as
// vertical, otherwise the toolbar gets a spurious shift when its bottom edge
// touches the endpoint of a rotated selection's side edge.
const ANGLE_EPSILON = 1e-9;

/**
 * Retrieves the bounding rectangle of an element.
 * @param {HTMLElement} element - The element to get the bounds for.
 * @returns {DOMRect} The bounding rectangle of the element.
 */
export const getBounds = (element) => element.getBoundingClientRect();

/**
 * Calculates the difference between an element's bounds and the stage's bounds.
 * @param {Bounds} elementBounds - The bounds of the element.
 * @param {Bounds} stageBounds - The bounds of the stage.
 * @returns {Bounds} The difference in bounds, zeroed to the top left of the stage.
 */
export const getDifferenceToStage = (elementBounds, stageBounds) => {
  return {
    left: elementBounds.left - stageBounds.left,
    top: elementBounds.top - stageBounds.top,
    right: elementBounds.right - stageBounds.left,
    bottom: elementBounds.bottom - stageBounds.top,
    width: elementBounds.width,
    height: elementBounds.height,
  };
};

/**
 * Returns a selection matrix based on the transformer's drawn tool, with a
 * buffer to give positioning space around the selection.
 * @param {object} props
 * @param {?{w: number, h: number, matrix: import("./BoundsHelpers.mjs").matrix, pageOffsetX: number, yOffset: number}} props.drawnTool - The transformer's drawn tool from DrawnToolStore, carrying the offsets baked into its matrix.
 * @param {object} props.block - The selected block from the block container.
 * @param {Array<Object>} props.selectedElements
 * @param {boolean} props.getTarget
 * @param {LAYOUTS[number]} props.layout
 * @param {number} props.yOffset
 * @param {number=} props.zoom
 *
 * @returns {MatrixBoundingCorners}
 */
export const getMatrixRelativeBoundingPoints = ({
  block,
  drawnTool,
  selectedElements,
  getTarget,
  layout,
  yOffset,
  zoom = 1,
}) => {
  let selectionMatrix;
  let rectDimensions;
  let stripX;
  let stripY;

  // Compute the matrix from selection store data when the caller asks for it
  // (getTarget, e.g. crop mode) or when there's no drawn tool because the
  // transformer isn't on screen (e.g. during text editing).
  if (getTarget || !drawnTool) {
    const target = getSelectedElementFullMatrix({
      block,
      selectedElements,
      layout,
      yOffset,
      zoom,
    });
    selectionMatrix = [...target.matrix];
    rectDimensions = [target.dimensions.w, target.dimensions.h];
    // strip the offsets baked into the matrix (measured just now), not the
    // pageOffsetX prop — the prop lags a reflow (browser zoom / resize)
    // until StageView re-renders, and the mismatch scoots the toolbar
    stripX = target.pageOffsetX;
    stripY = target.yOffset;
  } else {
    // copy: the offset below mutates, and the store's matrix must stay pristine
    selectionMatrix = [...drawnTool.matrix];
    rectDimensions = [drawnTool.w, drawnTool.h];
    // strip the offsets the tool carries (the ones baked into its matrix),
    // not the props — the pageOffsetX prop can lag a reflow (see DrawnToolStore)
    stripX = drawnTool.pageOffsetX;
    stripY = drawnTool.yOffset;
  }

  // Strip page/block offset → block-local coords; CSS re-adds via vars.
  selectionMatrix[4] -= stripX;
  selectionMatrix[5] -= stripY;

  // add buffer to draw the boundaries around the shape with buffer so it doesn't have to be added later
  const inMatrix = applyMatrixToObject(selectionMatrix, {
    center: [rectDimensions[0] / 2, rectDimensions[1] / 2],
    tl: [-ELEMENT_TOOLBAR_BUFFER, -ELEMENT_TOOLBAR_BUFFER],
    tr: [rectDimensions[0] + ELEMENT_TOOLBAR_BUFFER, -ELEMENT_TOOLBAR_BUFFER],
    br: [
      rectDimensions[0] + ELEMENT_TOOLBAR_BUFFER,
      rectDimensions[1] + ELEMENT_TOOLBAR_BUFFER,
    ],
    bl: [-ELEMENT_TOOLBAR_BUFFER, rectDimensions[1] + ELEMENT_TOOLBAR_BUFFER],
  });

  return inMatrix;
};

/**
 * Check to see if the toolbar bounds intersect the element (likely when rotated) and shift along the x axis accordingly.
 * @param {Array<SingleEdgeIntersectionResult>} intersectionCollection
 * @returns {Coordinates=}
 */
export const shiftToolbarFromIntersection = (intersectionCollection) => {
  // todo: note to change this if we do more than just top alignment
  const bottomIntersection = intersectionCollection.find(
    (intersection) => intersection.baseEdgeName === BOTTOM,
  );

  if (!bottomIntersection) return;

  const { point, baseEdge, comparisonEdge } = bottomIntersection;
  const [leftPoint] = baseEdge.sort((a, b) => a[0] - b[0]);

  const xShift = point[0] - leftPoint[0];
  const yShift = 0;

  const highestIntersectionEnd =
    comparisonEdge[0][1] > comparisonEdge[1][1]
      ? comparisonEdge[0]
      : comparisonEdge[1];
  const fromIntersection = vec2.sub(
    vec2.create(),
    highestIntersectionEnd,
    point,
  );
  const intersectionAngle = Math.atan2(
    fromIntersection[1],
    fromIntersection[0],
  );

  // return base positioning since there's effectively no intersection
  if (Math.abs(intersectionAngle - HALF_PI) < ANGLE_EPSILON) return;

  const sign = intersectionAngle <= HALF_PI ? -1 : 1;

  return [xShift !== 0 ? sign * xShift : xShift, yShift];
};

/**
 * Gets coordinates to elements center, then the intersection point relative to the vertical ray and selectionEdges.
 * Returns coordinates to center the element above the selection.
 * @param {MatrixBoundingCorners} selectionMatrixBounds
 * @param {BoundingEdges} selectionEdges,
 * @param {Object} selfBounds
 * @param {TOP} area - area to shift to. Just top is supported for now
 * @returns {Coordinates}
 *
 * @todo support other areas other than top
 */
export const adjustToSelection = (
  selectionMatrixBounds,
  selectionEdges,
  selfBounds,
  area = TOP,
) => {
  const verticalRayFromSelectionCenter = getVerticalRayFromCenter(
    selectionMatrixBounds,
    RAY_OFFSET_VALUE,
  );
  const selectionEdgeValues = Object.values(selectionEdges);
  const { point: coordinates } = getFirstEdgeIntersection(
    selectionEdgeValues,
    verticalRayFromSelectionCenter,
    CENTER,
  );

  if (area === TOP) {
    coordinates[1] -= selfBounds.height;
  }

  return coordinates;
};

/**
 * Calculates the position of an element relative to the stage, considering zoom.
 * @param {Object} props - The parameters for calculating the position.
 * @param {object} props.block - The selected block from the block container.
 * @param {?object} props.drawnTool - The transformer's drawn tool from DrawnToolStore (null when not drawn).
 * @param {boolean} props.getTarget - Collect target from selection store instead of from drawn transformer.
 * @param {LAYOUTS[number]} props.layout - The current selected layout.
 * @param {Array<object>} props.selectedElements - Array of selected element objects. Comes from selection store.
 * @param {HTMLElement} props.self - The element to position.
 * @param {HTMLElement} props.stage - The stage element.
 * @param {number} props.yOffset - Y offset from the top of the canvases container to the current canvas (in design space).
 * @param {number} props.zoom - The current zoom level.
 *
 * @returns {Coordinates}
 */
export const getPosition = ({
  block,
  drawnTool,
  getTarget,
  layout,
  selectedElements,
  yOffset,
  self,
  stage,
  zoom,
}) => {
  const stageBounds = getBounds(stage);
  const baseSelfBounds = getBounds(self);

  const selfBounds = getDifferenceToStage(baseSelfBounds, stageBounds);
  const selectionMatrixBounds = getMatrixRelativeBoundingPoints({
    block,
    drawnTool,
    getTarget,
    layout,
    selectedElements,
    yOffset,
    zoom,
  });
  const selectionEdges = collectEdgesFromCorners(selectionMatrixBounds);
  // send self bounds into zoomed space as it is in screen space
  const zoomedSelfBounds = objectToZoomedSpace(selfBounds, zoom);

  // move the position to outside the selection with buffer.
  const coordinates = adjustToSelection(
    selectionMatrixBounds,
    selectionEdges,
    zoomedSelfBounds,
  );

  // adjust self bounds with new position (design space, so the zoomed width)
  const adjustedSelfBounds = {
    ...zoomedSelfBounds,
    left: coordinates[0] - zoomedSelfBounds.width / 2,
    top: coordinates[1],
  };

  // get bound corners and edges after center/vertical placement
  const adjustedSelfBoundingPoints =
    getElementBoundingPoints(adjustedSelfBounds);
  const adjustedSelfEdges = collectEdgesFromCorners(adjustedSelfBoundingPoints);

  // intersection to shape buffer, likely from rotation
  const toolbarIntersectionCollection = collectEdgeIntersections(
    adjustedSelfEdges,
    selectionEdges,
  );

  if (toolbarIntersectionCollection) {
    const shiftAmounts = shiftToolbarFromIntersection(
      toolbarIntersectionCollection,
    );

    if (shiftAmounts) {
      vec2.sub(coordinates, coordinates, shiftAmounts);
    }
  }

  // todo: do this if people complain about large items going off the stage. Don't delete this.
  //  const scrollPositions = getScrollPositions(stage);
  //  const visibleStageBounds = getVisibleStageBounds(stageBounds, scrollPositions);
  //  const visibleStageWithBuffer = applyBounds(visibleStageBounds, STAGE_BUFFER);

  // const base = constrainToBounds({
  //   elementBounds: selfBounds,
  //   containerBounds: visibleStageWithBuffer,
  // });

  return coordinates;
};
